import React, { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { requiredCapabilityForPath } from "../../auth/routeCapabilities";

interface AuthGuardProps {
  children: ReactNode;
  requiredCapability?: string;
  requireAuth?: boolean;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredCapability,
  requireAuth = true,
}) => {
  const { isAuthenticated, user, hasCapability, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Redirect to login with return url
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  const routeCapability = requiredCapability || requiredCapabilityForPath(location.pathname);

  // If user is authenticated but doesn't have required capability
  if (isAuthenticated && routeCapability && !hasCapability(routeCapability)) {
    if (hasCapability("capability:admin_console")) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (hasCapability("capability:ops_dashboard")) {
      return <Navigate to="/staff/dashboard" replace />;
    }
    if (hasCapability("capability:customer_portal")) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // If user is authenticated and accessing login/register pages
  if (
    isAuthenticated &&
    (location.pathname.includes("/auth/login") ||
      location.pathname.includes("/auth/register"))
  ) {
    if (hasCapability("capability:admin_console")) return <Navigate to="/admin/dashboard" replace />;
    if (hasCapability("capability:ops_dashboard")) return <Navigate to="/staff/dashboard" replace />;
    if (hasCapability("capability:customer_portal")) return <Navigate to="/dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default AuthGuard;
